import React, { Component, ReactNode } from 'react';
import { Product, ReferencePrice, SkMaxPrice } from '~/Api/User/ProductGroup/SkMaxPrice';
import { formatCountriesError } from '~/UI';
import CountryIcon from '~/UI/CountryIcon';
import Heading from '~/UI/Heading';
import Price from '~/utils/Price';
import styles from './styles.less';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import { calculatePrice } from '../calculatePrice';
import { ProductValues } from '../ProductsTable/ProductsTable';
import memoizeOne from 'memoize-one';

interface Props {
	skMaxPrice: SkMaxPrice;
	productValues: { [id: string]: ProductValues };
	referencePackageSize: number | null;
	formatPercentage(percentage: number): string;
}

export default class Calculation extends Component<Props> {
	private renderSamePackageSizeMessage() {
		const {
			skMaxPrice: { finalCalculation, products },
		} = this.props;
		const diffPackageSizeCount =
			finalCalculation.threeLowestPricesAvg?.fromProducts.reduce(
				(count, product) => count + (product.samePackageSize ? 0 : 1),
				0
			) ?? 0;
		const samePackageSizeCount =
			(finalCalculation.threeLowestPricesAvg?.fromProducts.length ?? 0) - diffPackageSizeCount;

		switch (true) {
			case diffPackageSizeCount === 0:
				return <small>Average of 3 lowest recalculated ex-factory prices</small>;
			case diffPackageSizeCount === 1 && samePackageSizeCount === 0:
				return <small>1st lowest price was recalculated from products with different package sizes.</small>;
			case diffPackageSizeCount === 1 && samePackageSizeCount === 1:
				return <small>2nd lowest price was recalculated from products with different package sizes.</small>;
			case diffPackageSizeCount === 1 && samePackageSizeCount === 2:
				return <small>3rd lowest price was recalculated from products with different package sizes.</small>;
			case diffPackageSizeCount === 2 && samePackageSizeCount === 0:
				return <small>1st and 2nd lowest prices were recalculated from products with different package sizes.</small>;
			case diffPackageSizeCount === 2 && samePackageSizeCount === 1:
				return <small>2nd and 3rd lowest prices were recalculated from products with different package sizes.</small>;
			case diffPackageSizeCount === 3:
				return <small>All lowest prices were recalculated from products with different package sizes.</small>;
			default:
				return null;
		}
	}

	renderEmptyResultReason(): ReactNode {
		const {
			skMaxPrice: { finalCalculation },
		} = this.props;
		const countryError = finalCalculation.requiresOnlyOneProductPerCountry();

		if (countryError !== null) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one product for each country
					{formatCountriesError(countryError.duplicateCountries)}
				</strong>
			);
		}
		if (finalCalculation.requiresProductSettings()) {
			return <strong className={styles.rowError}>Some products settings are missing</strong>;
		}
		if (finalCalculation.requiresAtLeastOneReferenceCountryProduct()) {
			return <strong className={styles.rowError}>Calculation requires at least one reference country product</strong>;
		}
		if (finalCalculation.requiresReferenceSettings()) {
			return <strong className={styles.rowError}>Calculation requires reference settings</strong>;
		}
		if (finalCalculation.illegalPriceTypeSelected()) {
			return <strong className={styles.rowError}>Some products have illegal price selected</strong>;
		}
		return null;
	}

	resolveReferencePrice = memoizeOne(
		(type: string | null, skMaxPriceProduct: Product | undefined): ReferencePrice | null => {
			if (type === null) {
				return null;
			}
			return (
				skMaxPriceProduct?.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ??
				null
			);
		}
	);

	renderCalculatedPrice(id: string) {
		const { skMaxPrice, productValues, referencePackageSize } = this.props;

		const packageSize = productValues[id].packageSize;

		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;

		if (parsedPackageSize === null) {
			return null;
		}

		const referencePrice = skMaxPrice.products
			.find((x) => x.product.id === id)
			?.referencePrices.find((x) => x.calculatedPrice.selected);

		if (!referencePrice) return;
		return calculatePrice(
			referencePrice.calculatedPrice.targetCurrencyPrice,
			parsedPackageSize,
			referencePackageSize
		).roundedHumanFormat();
	}

	renderCalculatedERCPrice(id: string) {
		const { skMaxPrice, productValues, referencePackageSize } = this.props;

		const packageSize = productValues[id].packageSize;
		const referencePrice = this.resolveReferencePrice(
			productValues[id].referencePrice.value?.value ?? null,
			skMaxPrice.products.find((x) => x.product.id === id)
		);
		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;

		if (parsedPackageSize === null) {
			return null;
		}

		if (!referencePrice) return;
		const calcPrice = calculatePrice(
			referencePrice.calculatedPrice.targetCurrencyPrice,
			parsedPackageSize,
			referencePackageSize
		);
		const fxComparisonReferencePrice = skMaxPrice.products.find((x) => x.product.id === id)?.fxComparisonReferencePrice
			?.calculatedPrice.targetCurrencyPrice;

		if (!fxComparisonReferencePrice) return;
		const calcFxComparisonReferencePrice = calculatePrice(
			fxComparisonReferencePrice,
			parsedPackageSize,
			referencePackageSize
		);

		const fxComparisonResolution = skMaxPrice.products.find((x) => x.product.id === id)?.fxComparisonResolution;

		if (fxComparisonResolution === 'non-applicable') return calcPrice.roundedHumanFormat();

		return calcPrice.value >= calcFxComparisonReferencePrice.value
			? calcPrice.roundedHumanFormat()
			: calcFxComparisonReferencePrice.roundedHumanFormat();
	}

	render() {
		const {
			skMaxPrice: { finalCalculation, products },
			formatPercentage,
		} = this.props;

		const isErcEqual =
			!!finalCalculation.threeLowestPricesAvg &&
			finalCalculation.threeLowestPricesAvg.ercPrice.value === finalCalculation.threeLowestPricesAvg.price.value;

		const isErcHigher =
			!!finalCalculation.threeLowestPricesAvg &&
			finalCalculation.threeLowestPricesAvg.ercPrice.value > finalCalculation.threeLowestPricesAvg.price.value;

		return (
			<div className={styles.container}>
				<div className={styles.columns}>
					{finalCalculation.emptyResultReason.length > 0 ? (
						<div className={styles.row}>
							<p>{this.renderEmptyResultReason()}</p>
						</div>
					) : (
						<>
							<div className={styles.leftColumn}>
								<Heading level={3}>Lowest recalculated ex-factory prices:</Heading>
								{finalCalculation.threeLowestPricesAvg?.fromProducts.map((product, i) => (
									<div
										className={styles.row}
										data-product-id={product.id}
										data-testid={`lowest-exfactory-price-${i}`}
										key={product.id}
									>
										<div className={styles.rowItem}>
											<div className={styles.countryItem}>
												<span className={styles.number}>{i + 1}.</span>
												<div className={styles.countryIcon}>
													<CountryIcon
														code={product.dataSource.country.alpha2}
														name={product.dataSource.country.name}
													/>
												</div>
												<div className={styles.countryText}>
													<span className={styles.countryDescription}>
														<strong>{product.dataSource.country.alpha2}</strong> {product.dataSource.shortName}
													</span>
												</div>
											</div>
											<div className={styles.rowPrice}>
												{this.renderCalculatedPrice(products.filter((x) => x.product.id === product.id)[0].product.id)}
											</div>
										</div>
									</div>
								))}
								<div className={styles.rowTr}>
									<div className={styles.rowItem}>
										<div className={styles.tableItem}>
											<strong>Current price:</strong>
										</div>
										<div className={styles.rowPrice}>
											<strong>{finalCalculation.referencedProduct?.currentExFactoryPrice?.roundedHumanFormat()}</strong>
										</div>
									</div>
								</div>
								<div className={styles.rowTr}>
									<div className={styles.rowItem}>
										<div className={styles.tableItem}>
											<strong>Average prices:</strong>
											{this.renderSamePackageSizeMessage()}
										</div>
										<div className={styles.rowPrice}>
											<strong>{finalCalculation.threeLowestPricesAvg?.price.roundedHumanFormat()}</strong>
											{!isErcEqual && !isErcHigher && (
												<div className={styles.rowPriceArrowTop}>
													<svg
														xmlns='http://www.w3.org/2000/svg'
														viewBox='0 0 20 20'
														fill='currentColor'
														className='w-5 h-5'
													>
														<path
															fillRule='evenodd'
															d='M10 18a.75.75 0 01-.75-.75V4.66L7.3 6.76a.75.75 0 11-1.1-1.02l3.25-3.5a.75.75 0 011.1 0l3.25 3.5a.75.75 0 01-1.1 1.02l-1.95-2.1v12.59A.75.75 0 0110 18z'
															clipRule='evenodd'
														/>
													</svg>
												</div>
											)}
											{!isErcEqual && isErcHigher && (
												<div className={styles.rowPriceArrowBottom}>
													<svg
														xmlns='http://www.w3.org/2000/svg'
														viewBox='0 0 20 20'
														fill='currentColor'
														className='w-5 h-5'
													>
														<path
															fillRule='evenodd'
															d='M10 2a.75.75 0 01.75.75v12.59l1.95-2.1a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 111.1-1.02l1.95 2.1V2.75A.75.75 0 0110 2z'
															clipRule='evenodd'
														/>
													</svg>
												</div>
											)}
										</div>
									</div>
								</div>
								<div className={styles.rowTrLast}>
									<div className={styles.rowItem}>
										<div className={styles.tableItem}>
											<strong>Price drop:</strong>
										</div>
										<div className={styles.rowPrice}>
											<strong>
												{finalCalculation.priceDropPercents
													? formatPercentage(finalCalculation.priceDropPercents)
													: null}
											</strong>
										</div>
									</div>
								</div>
							</div>
							<div className={styles.secondColumn}>
								<Heading level={3}>ERC Lowest prices:</Heading>
								{finalCalculation.threeLowestPricesAvg?.ercFromProducts.map((product, i) => (
									<div
										className={styles.row}
										data-product-id={product.id}
										data-testid={`lowest-erc-price-${i}`}
										key={product.id}
									>
										<div className={styles.rowItem}>
											<div className={styles.countryItem}>
												<span className={styles.number}>{i + 1}.</span>
												<div className={styles.countryIcon}>
													<CountryIcon
														code={product.dataSource.country.alpha2}
														name={product.dataSource.country.name}
													/>
												</div>
												<div className={styles.countryText}>
													<span className={styles.countryDescription}>
														<strong>{product.dataSource.country.alpha2}</strong> {product.dataSource.shortName}
													</span>
												</div>
											</div>
											<div className={styles.rowPrice}>
												{this.renderCalculatedERCPrice(
													products.filter((x) => x.product.id === product.id)[0].product.id
												)}
											</div>
										</div>
									</div>
								))}

								<div className={styles.rowTr}>
									<div className={styles.rowItem}>
										<div className={styles.tableItem}>
											<strong>Current price:</strong>
										</div>
										<div className={styles.rowPrice}>
											<strong>{finalCalculation.referencedProduct?.currentExFactoryPrice?.roundedHumanFormat()}</strong>
										</div>
									</div>
								</div>
								<div className={styles.rowTr}>
									<div className={styles.rowItem}>
										<div className={styles.tableItem}>
											<strong>ERC price:</strong>
											<small>{`Price on the selected date was ${
												isErcHigher ? 'higher' : 'lower'
											} than today, due to the exchange rates.`}</small>
										</div>
										<div className={styles.rowPrice}>
											<strong>{finalCalculation.threeLowestPricesAvg?.ercPrice.roundedHumanFormat()}</strong>
											{!isErcEqual && isErcHigher && (
												<div className={styles.rowPriceArrowTop}>
													<svg
														xmlns='http://www.w3.org/2000/svg'
														viewBox='0 0 20 20'
														fill='currentColor'
														className='w-5 h-5'
													>
														<path
															fillRule='evenodd'
															d='M10 18a.75.75 0 01-.75-.75V4.66L7.3 6.76a.75.75 0 11-1.1-1.02l3.25-3.5a.75.75 0 011.1 0l3.25 3.5a.75.75 0 01-1.1 1.02l-1.95-2.1v12.59A.75.75 0 0110 18z'
															clipRule='evenodd'
														/>
													</svg>
												</div>
											)}
											{!isErcEqual && !isErcHigher && (
												<div className={styles.rowPriceArrowBottom}>
													<svg
														xmlns='http://www.w3.org/2000/svg'
														viewBox='0 0 20 20'
														fill='currentColor'
														className='w-5 h-5'
													>
														<path
															fillRule='evenodd'
															d='M10 2a.75.75 0 01.75.75v12.59l1.95-2.1a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 111.1-1.02l1.95 2.1V2.75A.75.75 0 0110 2z'
															clipRule='evenodd'
														/>
													</svg>
												</div>
											)}
										</div>
									</div>
								</div>
								<div className={styles.rowTrLast}>
									<div className={styles.rowItem}>
										<div className={styles.tableItem}>
											<strong>ERC price drop:</strong>
										</div>
										<div className={styles.rowPrice}>
											<strong>
												{finalCalculation.ercPriceDropPercents
													? formatPercentage(finalCalculation.ercPriceDropPercents)
													: null}
											</strong>
										</div>
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		);
	}
}
